import React, { useState, useMemo } from 'react';
import { ChevronRight, Globe, ShoppingCart, BookOpen, Settings2, Users, Play, MapPin, AlertTriangle, Plus, ChevronDown } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface SiteCategory {
  id: string;
  name: string;
  description: string;
  isAllowed: boolean;
}

interface ParentCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  subcategories: string[];
}

// Parent categories with their subcategory mappings
const PARENT_CATEGORIES: ParentCategory[] = [
  {
    id: 'jewish',
    name: 'Jewish',
    description: 'Jewish-focused content including news, learning, entertainment, and music',
    icon: Globe,
    subcategories: ['Jewish News', 'Jewish Learning & Education', 'Jewish Entertainment', 'Jewish Music'],
  },
  {
    id: 'shopping',
    name: 'Shopping',
    description: 'Online shopping and retail websites',
    icon: ShoppingCart,
    subcategories: ['General Shopping', "Women's General Shopping", "Women's Swimwear & Intimate Clothing"],
  },
  {
    id: 'information',
    name: 'Information & Resources',
    description: 'Educational content, reference materials, and informational sites',
    icon: BookOpen,
    subcategories: ['Reference', 'Education', 'Health and Medicine', 'Other Religions', 'Blogs', 'Food & Drink Information'],
  },
  {
    id: 'productivity',
    name: 'Productivity',
    description: 'Tools and services for work and productivity',
    icon: Settings2,
    subcategories: ['General Tools', 'Digital Content Creation', 'Search Engines', 'AI Chat', 'Stock Photo Searching'],
  },
  {
    id: 'communication',
    name: 'Communication',
    description: 'Social media, messaging, and email platforms',
    icon: Users,
    subcategories: ['Social Media', 'Video and Messaging', 'Email'],
  },
  {
    id: 'entertainment',
    name: 'Entertainment',
    description: 'Gaming, streaming, news, sports, and lifestyle content',
    icon: Play,
    subcategories: ['Gaming', 'Movies and TV', 'Music and Audio Streaming', 'Video Streaming Platforms', 'News (Non-Jewish)', 'Sports', 'Style & Fashion', 'Celebrity'],
  },
  {
    id: 'utilities',
    name: 'Day-to-Day Utilities',
    description: 'Essential services for daily life including travel, delivery, and banking',
    icon: MapPin,
    subcategories: ['Travel & Experiences', 'Food & Grocery Delivery', 'Banking', 'Government Portals', 'Navigation & Ride Sharing'],
  },
  {
    id: 'sensitive',
    name: 'Sensitive Topics',
    description: 'Content that may require additional oversight or restriction',
    icon: AlertTriangle,
    subcategories: ['Gambling', 'Drugs Alcohol & Tobacco', 'Piracy & Illegal Downloads', 'Defacement', 'Violence'],
  },
];

interface WebsiteCategoryGroupsProps {
  categories: SiteCategory[];
  onCategoryToggle: (categoryName: string, currentlyAllowed: boolean) => void;
  canChangeDirectly: boolean;
}

const WebsiteCategoryGroups: React.FC<WebsiteCategoryGroupsProps> = ({
  categories,
  onCategoryToggle,
  canChangeDirectly,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Create a map of category name -> category data for quick lookup
  const categoryMap = useMemo(() => {
    const map: Record<string, SiteCategory> = {};
    categories.forEach(cat => {
      map[cat.name] = cat;
    });
    return map;
  }, [categories]);

  // Calculate group status based on subcategories
  const getGroupStatus = (parentCategory: ParentCategory) => {
    const subcats = parentCategory.subcategories
      .map(name => categoryMap[name])
      .filter(Boolean);
    
    const allowedCount = subcats.filter(c => c.isAllowed).length;
    const blockedCount = subcats.filter(c => !c.isAllowed).length;
    const total = subcats.length;
    
    // All allowed = fully on, all blocked = fully off, mixed = partial
    const isFullyAllowed = allowedCount === total && total > 0;
    const isFullyBlocked = blockedCount === total && total > 0;
    
    return {
      allowedCount,
      blockedCount,
      total,
      isFullyAllowed,
      isFullyBlocked,
      isMixed: !isFullyAllowed && !isFullyBlocked && total > 0,
    };
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleParentToggle = (parentCategory: ParentCategory) => {
    const status = getGroupStatus(parentCategory);
    
    // If currently all allowed, block all. Otherwise, allow all.
    const newAction = status.isFullyAllowed ? 'block' : 'allow';
    
    // Toggle all subcategories
    parentCategory.subcategories.forEach(subName => {
      const cat = categoryMap[subName];
      if (cat) {
        if (newAction === 'block' && cat.isAllowed) {
          onCategoryToggle(subName, true); // currently allowed, block it
        } else if (newAction === 'allow' && !cat.isAllowed) {
          onCategoryToggle(subName, false); // currently blocked, allow it
        }
      }
    });
  };

  return (
    <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
      <div className="px-4 py-4 border-b border-border/50">
        <h3 className="font-semibold text-[16px] text-foreground">Website Categories</h3>
        <p className="text-[13px] text-muted-foreground mt-1">
          Toggle categories on (green) or off (red). Tap any category to see subcategories.
        </p>
      </div>

      <div className="divide-y divide-border/50">
        {PARENT_CATEGORIES.map((parent) => {
          const Icon = parent.icon;
          const status = getGroupStatus(parent);
          const isExpanded = expandedGroups.includes(parent.id);
          const hasSubcategories = status.total > 0;

          return (
            <Collapsible
              key={parent.id}
              open={isExpanded}
              onOpenChange={() => hasSubcategories && toggleGroup(parent.id)}
            >
              <div className="flex items-center gap-3 px-4 py-4">
                {/* Icon */}
                <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>

                {/* Content */}
                <CollapsibleTrigger asChild disabled={!hasSubcategories}>
                  <button className="flex-1 text-left flex items-center gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[15px] text-foreground">{parent.name}</p>
                      <p className="text-[12px] text-muted-foreground line-clamp-1">{parent.description}</p>
                    </div>
                  </button>
                </CollapsibleTrigger>

                {/* Status indicator - always show for groups with subcategories */}
                {hasSubcategories && (
                  <span className="text-[12px] font-medium flex-shrink-0">
                    <span className="text-green-600 dark:text-green-500">{status.allowedCount}</span>
                    <span className="text-muted-foreground mx-1">|</span>
                    <span className="text-destructive">{status.blockedCount}</span>
                  </span>
                )}
              </div>

              {/* Subcategories */}
              <CollapsibleContent>
                <div className="pl-4 pb-3 space-y-2">
                  {parent.subcategories.map((subName) => {
                    const subcat = categoryMap[subName];
                    if (!subcat) return null;

                    return (
                      <div
                        key={subName}
                        className={`mx-2 px-4 py-3 rounded-xl border flex items-center justify-between ${
                          subcat.isAllowed
                            ? 'bg-primary/5 border-primary/20'
                            : 'bg-destructive/5 border-destructive/20'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-medium text-foreground">{subcat.name}</p>
                          {subcat.description && (
                            <p className="text-[11px] text-muted-foreground line-clamp-1">{subcat.description}</p>
                          )}
                        </div>
                        <Switch
                          checked={subcat.isAllowed}
                          onCheckedChange={() => canChangeDirectly && onCategoryToggle(subcat.name, subcat.isAllowed)}
                          colorWhenOff="destructive"
                          colorWhenOn="success"
                          className="flex-shrink-0"
                          disabled={!canChangeDirectly}
                        />
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {/* Footer for adding specific sites */}
      <Collapsible>
        <CollapsibleTrigger asChild>
          <button className="w-full px-4 py-3 flex items-center gap-2 text-muted-foreground hover:bg-muted/30 transition-colors border-t border-border/50">
            <Plus className="w-4 h-4" />
            <span className="text-[13px]">Need to allow or block a specific website?</span>
            <ChevronDown className="w-4 h-4 ml-auto" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 py-3 bg-muted/20 text-[13px] text-muted-foreground">
          Use the "Specific Sites" tab to add individual websites to your whitelist or blacklist.
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default WebsiteCategoryGroups;
